import React from 'react'
import { connect } from 'react-redux'
import { NavLink, Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom'
import {
  AppBar,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Toolbar,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import SubcontractorDashboard from './SubcontractorDashboard'
import SubcontractorSchedule from './SubcontractorSchedule'
import SubcontractorUsers from './SubcontractorUsers'

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    background: 'radial-gradient(circle at top, rgba(230, 239, 233, 0.92) 0%, rgba(242, 245, 239, 0.96) 36%, #f6f4ee 100%)'
  },
  appBar: {
    background: 'linear-gradient(90deg, #14382d 0%, #1b4d3c 55%, #315b47 100%)',
    boxShadow: '0 10px 30px rgba(20, 56, 45, 0.18)'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    minHeight: 74
  },
  brandWrap: {
    display: 'flex',
    flexDirection: 'column'
  },
  overline: {
    color: 'rgba(232, 242, 235, 0.82)',
    letterSpacing: '0.12em'
  },
  headline: {
    fontWeight: 700,
    color: '#fff'
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap'
  },
  shell: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(5)
  },
  introCard: {
    borderRadius: 24,
    overflow: 'hidden',
    marginBottom: theme.spacing(3),
    background: 'linear-gradient(135deg, rgba(18, 53, 42, 0.98) 0%, rgba(28, 83, 63, 0.96) 60%, rgba(151, 121, 58, 0.95) 100%)',
    boxShadow: '0 22px 48px rgba(20, 56, 45, 0.2)'
  },
  introContent: {
    padding: theme.spacing(4)
  },
  introEyebrow: {
    color: 'rgba(236, 244, 239, 0.82)',
    letterSpacing: '0.12em'
  },
  introTitle: {
    color: '#fff',
    fontWeight: 700,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1)
  },
  introDescription: {
    color: 'rgba(244, 248, 245, 0.92)',
    maxWidth: 720
  },
  identityWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5)
  },
  chip: {
    fontWeight: 700
  },
  primaryChip: {
    backgroundColor: 'rgba(255,255,255,0.18)',
    color: '#fff'
  },
  secondaryChip: {
    backgroundColor: 'rgba(11, 22, 18, 0.2)',
    color: '#f2f6f3',
    border: '1px solid rgba(255,255,255,0.18)'
  },
  metaCard: {
    borderRadius: 18,
    background: 'rgba(250, 252, 250, 0.12)',
    border: '1px solid rgba(255,255,255,0.14)',
    padding: theme.spacing(2)
  },
  metaLabel: {
    color: 'rgba(236, 244, 239, 0.76)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: '0.7rem',
    fontWeight: 700
  },
  metaValue: {
    color: '#fff',
    fontWeight: 700,
    marginTop: theme.spacing(0.75)
  },
  metaCaption: {
    color: 'rgba(244, 248, 245, 0.82)',
    marginTop: theme.spacing(0.75)
  },
  navWrap: {
    marginTop: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  navButton: {
    color: '#e8f2eb',
    borderColor: 'rgba(232, 242, 235, 0.28)',
    borderRadius: 999,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  navButtonActive: {
    backgroundColor: '#fff',
    color: '#173f31',
    borderColor: '#fff'
  },
  logoutButton: {
    color: '#fff',
    borderColor: 'rgba(255,255,255,0.28)'
  },
  sectionWrap: {
    marginTop: theme.spacing(1)
  }
}))

const navItems = [
  {
    label: 'Dashboard',
    slug: '',
    description: 'View subcontractor scope, upcoming work, and portal access details.'
  },
  {
    label: 'Schedule',
    slug: '/schedule',
    description: 'Review the herbicide work that is visible to your crews and filter by status.'
  },
  {
    label: 'Users',
    slug: '/users',
    description: 'See who currently has portal access across your subcontractor scope.'
  }
]

function getCurrentNavItem (pathname, basePath) {
  const exactBase = pathname === basePath
  if (exactBase) {
    return navItems[0]
  }

  return navItems.find(item => item.slug && pathname.startsWith(`${basePath}${item.slug}`)) || navItems[0]
}

const SubcontractorPortalApp = ({ user }) => {
  const classes = useStyles()
  const location = useLocation()
  const { path, url } = useRouteMatch()
  const profile = user?.subcontractorProfile || {}
  const currentItem = getCurrentNavItem(location.pathname, path)
  const capabilityChips = [
    'Schedule visibility',
    'Crew-scoped portal access',
    profile?.capabilities?.canManageUsers ? 'User management' : null
  ].filter(Boolean)

  return (
    <div className={classes.root}>
      <AppBar position="static" elevation={0} className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <div className={classes.brandWrap}>
            <Typography variant="overline" className={classes.overline}>
              Subcontractor Portal
            </Typography>
            <Typography variant="h6" className={classes.headline}>
              {profile.vendorName || 'Spray Contractor Workspace'}
            </Typography>
          </div>

          <div className={classes.toolbarActions}>
            <Chip
              label={(profile.role || 'applicator').toUpperCase()}
              className={`${classes.chip} ${classes.primaryChip}`}
            />
            <Chip
              label={profile.crewName ? `Crew ${profile.crewName}` : 'All subcontractor crews'}
              className={`${classes.chip} ${classes.secondaryChip}`}
            />
            <Button href="/logout" variant="outlined" className={classes.logoutButton}>
              Logout
            </Button>
          </div>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" className={classes.shell}>
        <Paper className={classes.introCard} elevation={0}>
          <div className={classes.introContent}>
            <Grid container spacing={3} alignItems="stretch">
              <Grid item xs={12} md={7}>
                <Typography variant="overline" className={classes.introEyebrow}>
                  Limited Contractor Workspace
                </Typography>
                <Typography variant="h4" className={classes.introTitle}>
                  {currentItem.label}
                </Typography>
                <Typography variant="body1" className={classes.introDescription}>
                  {currentItem.description}
                </Typography>

                <div className={classes.navWrap}>
                  {navItems.map((item) => (
                    <Button
                      key={item.label}
                      component={NavLink}
                      exact={item.slug === ''}
                      to={`${url}${item.slug}`}
                      variant="outlined"
                      className={classes.navButton}
                      activeClassName={classes.navButtonActive}
                    >
                      {item.label}
                    </Button>
                  ))}
                </div>
              </Grid>

              <Grid item xs={12} md={5}>
                <div className={classes.identityWrap}>
                  <Box display="flex" gridGap={8} flexWrap="wrap">
                    <Chip
                      label={profile.displayName || user?.name || 'Portal user'}
                      className={`${classes.chip} ${classes.primaryChip}`}
                    />
                    <Chip
                      label={(profile.accessStatus || 'active').replace(/_/g, ' ')}
                      className={`${classes.chip} ${classes.secondaryChip}`}
                    />
                    {(profile.subcontractorTypes || []).map((type) => (
                      <Chip
                        key={type}
                        label={type.replace(/_/g, ' ')}
                        className={`${classes.chip} ${classes.secondaryChip}`}
                      />
                    ))}
                  </Box>
                </div>
              </Grid>

              <Grid item xs={12} md={4}>
                <div className={classes.metaCard}>
                  <Typography className={classes.metaLabel}>Operating Scope</Typography>
                  <Typography variant="h6" className={classes.metaValue}>
                    {profile.role === 'admin' ? 'Vendor-wide portal view' : (profile.crewName || 'Crew assignment pending')}
                  </Typography>
                  <Typography variant="body2" className={classes.metaCaption}>
                    {profile.role === 'admin'
                      ? 'Admin accounts can view all crews tied to this subcontractor organization.'
                      : 'Applicators only see the crew and territories assigned through the subcontractor setup.'}
                  </Typography>
                </div>
              </Grid>

              <Grid item xs={12} md={4}>
                <div className={classes.metaCard}>
                  <Typography className={classes.metaLabel}>Portal Landing</Typography>
                  <Typography variant="h6" className={classes.metaValue}>
                    {(profile.defaultLanding || 'subcontractor_dashboard').replace(/_/g, ' ')}
                  </Typography>
                  <Typography variant="body2" className={classes.metaCaption}>
                    This workspace stays focused on schedule visibility and the user list already approved for subcontractors.
                  </Typography>
                </div>
              </Grid>

              <Grid item xs={12} md={4}>
                <div className={classes.metaCard}>
                  <Typography className={classes.metaLabel}>Available Tools</Typography>
                  <Box mt={1.5} display="flex" gridGap={8} flexWrap="wrap">
                    {capabilityChips.map((item) => (
                      <Chip key={item} label={item} className={`${classes.chip} ${classes.secondaryChip}`} />
                    ))}
                  </Box>
                </div>
              </Grid>
            </Grid>
          </div>
        </Paper>

        <div className={classes.sectionWrap}>
          <Switch>
            <Route path={path} exact component={SubcontractorDashboard} />
            <Route path={`${path}/schedule`} component={SubcontractorSchedule} />
            <Route path={`${path}/users`} component={SubcontractorUsers} />
            <Redirect to={path} />
          </Switch>
        </div>
      </Container>
    </div>
  )
}

const mapStateToProps = (state) => ({
  user: state.user
})

export default connect(mapStateToProps)(SubcontractorPortalApp)
